import { useState } from "react";
import { motion } from "framer-motion";
import { slugify } from "@/utils/string.ts";
import { Checkbox } from "@/components/ui/Checkbox.tsx";
import { openClose } from "@/utils/motion/open-close.ts";
import { useNavigate } from "react-router-dom";
import { feedbackOptions } from "@/data/static/agenda.ts";
import { FEEDBACK_EVENT_ID } from "@/utils/agenda.ts";
import { FeedbackProps, ITypes } from "@/types";
import { useTranslation, Trans } from "react-i18next";
import { useCreateAgendaFeedbackMutation } from "@/data/feedback.ts";
import Label from "@/components/ui/forms/Label.tsx";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button";

interface IProps {
  type: ITypes;
  comment: string | undefined;
  feedback: FeedbackProps;
  activeTab: number;
  setActiveTab: (tab: number) => void;
  feedbackAreas: Array<keyof typeof feedbackOptions>;
}

const MobileFeedbackFourthPage = ({
  type,
  comment,
  feedback,
  activeTab,
  setActiveTab,
  feedbackAreas,
}: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const options = feedbackAreas;

  const [index, setIndex] = useState(0);
  const [payload, setPayload] = useState({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const { mutate, isLoading } = useCreateAgendaFeedbackMutation(() => {
    localStorage.removeItem(FEEDBACK_EVENT_ID);
    navigate(-1);
  });

  const nextOption = () => {
    const key = options[index];

    if (index < options.length - 1) {
      setPayload((r) => {
        const l = { ...r };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        l[key] = selectedOptions;
        return l;
      });

      setSelectedOptions([]);
      setIndex((r) => r + 1);
    } else {
      const improvements = { ...payload, [key]: selectedOptions };

      mutate({
        ...feedback,
        improvements: JSON.stringify(improvements),
        first_feedback: comment!,
      });
    }
  };

  const getFeedback = (idx: number) => {
    const currentOption = options[idx];

    if (currentOption === "Topic and organization") {
      return (
        feedbackOptions["Topic and organization"][
          type.toLowerCase() as keyof (typeof feedbackOptions)["Topic and organization"]
        ] || []
      );
    }

    return feedbackOptions[currentOption] || [];
  };

  const handleChange = (checked: boolean | string, name: string) => {
    let exSelected = [...selectedOptions];

    if (exSelected.includes(name)) {
      if (!checked) {
        exSelected = exSelected.filter((r) => r !== name);
      }
    } else {
      exSelected.push(name);
    }

    setSelectedOptions(exSelected);
  };

  return (
    <motion.div
      variants={openClose()}
      className="px-3 py-10 flex flex-col items-center justify-center gap-y-6 w-full h-full"
    >
      <h4 className="text-xl font-semibold text-left text-brand-secondary">
        {t("feedback.problem_with")}&nbsp;
        <b className="text-brand-secondary font-semibold">{options[index]}?</b>
      </h4>

      <div className="w-full space-y-4">
        <ul className="flex flex-col space-y-2 w-full">
          {getFeedback(index).map((r: string) => (
            <li
              key={slugify(t(r))}
              className="gap-2 flex flex-row items-center justify-start w-full"
            >
              <Checkbox
                id={slugify(t(r))}
                name={t(r)}
                checked={selectedOptions.includes(t(r, { lng: "en" }))}
                onCheckedChange={(check) =>
                  handleChange(check, t(r, { lng: "en" }))
                }
              />

              <Label
                htmlFor={slugify(t(r))}
                className="text-lg text-edu-active font-normal"
              >
                <Trans i18nKey={r} components={{ b: <strong /> }} />
              </Label>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-center gap-x-2 w-full">
        <Button
          size="md"
          onClick={() => setActiveTab(activeTab > 0 ? activeTab - 1 : 0)}
        >
          {t("btns.go")} {t("btns.back")}
        </Button>

        <LoadingButton
          size="md"
          color="green"
          loading={isLoading}
          onClick={nextOption}
          disabled={
            !selectedOptions || selectedOptions.length === 0 || isLoading
          }
          className="flex-grow"
        >
          {t("btns.send")}
        </LoadingButton>
      </div>
    </motion.div>
  );
};

export default MobileFeedbackFourthPage;
